// Admin-only security-alerts list ("N security events to review") — server contract.
//
// Detection scans the FULL 30-day lookback every time (no review watermark, so
// a burst split across a review still alerts — KAN-662). Relevance and review
// are PER PERSON (KAN-661/662, RD-SEC-046/047): the viewer's own actions and
// invites/promotions about them are dropped, alerts older than their join date
// are dropped, and alerts they marked reviewed are dropped via per-user
// `user_notice_receipts` (`security_alert:<kind>:<audit row uuid>`, see
// utils/notices/securityAlertReceipts.js) — never the retired company-wide
// `security_event_reviews` / `mark_security_events_reviewed`.
//
// Alert shape: { id, kind, severity: 'high'|'medium', headline: {id, values},
// lastSeen, activity: { entityType, eventType }, actorId?, subjectUserId? }.
// `id` is the stable receipt key. The detection rules live in
// securityEventRules.js / securityEventRulesPolicy.js, the viewer filter in
// securityEventRelevance.js; all are re-exported below.

import { supabase } from '../supabase';
import {
  findSlidingWindowBurst,
  findAllSlidingWindowBursts,
  detectMassVoid,
  detectRoleEscalation,
  detectAdminInvites,
  detectMfaDisabled,
  detectBulkCatalogDelete,
  detectLoginFailedBurst,
  sortSecurityEvents,
} from './securityEventRules';
import {
  detectOwnershipTransferred,
  detectAdminDeactivated,
  detectAdminRemoved,
  detectMfaPolicyChanged,
} from './securityEventRulesPolicy';
import { detectLargeCreditGrant, detectCreditLimitsRaised } from './securityEventRulesCredit';
import { filterEventsForViewer, collectMissingNameIds, fillEventNames } from './securityEventRelevance';
import {
  readSecurityAlertReviewed,
  markSecurityAlertsReviewed,
  newestSecurityAlertReview,
} from '../utils/notices/securityAlertReceipts';
import { fetchViewerIdentity, fetchMembershipDirectory, fetchNamesByUserId } from './securityEventsDirectory';
import { subscribeNoticeReceipts } from '../utils/notices/noticeReceiptsBus';

export {
  detectOwnershipTransferred,
  detectAdminDeactivated,
  detectAdminRemoved,
  detectMfaPolicyChanged,
  detectLargeCreditGrant,
  detectCreditLimitsRaised,
  filterEventsForViewer,
  findSlidingWindowBurst,
  findAllSlidingWindowBursts,
  detectMassVoid,
  detectRoleEscalation,
  detectAdminInvites,
  detectMfaDisabled,
  detectBulkCatalogDelete,
  detectLoginFailedBurst,
  sortSecurityEvents,
};

const DAY_MS = 24 * 60 * 60 * 1000;
const LOOKBACK_DAYS = 30;

// PostgREST's default max-rows cap. A rule whose fetch comes back at exactly
// this length may be missing older (or, since fix 2, newer-than-cutoff)
// rows — we never hide that from the caller (see `truncated` below).
const POSTGREST_ROW_CAP = 1000;

// The (entity_type, event_type) pairs the 12 detection queries watch, in the
// exact order `runSecurityDetection` fetches them. This is the ONE place in
// JS that lists the pairs — the `security_event_pings` upsert trigger
// (supabase/migrations/20260926140000_security_event_pings.sql) mirrors this
// list by hand in its `WHEN` clause so a ping fires for exactly the same
// writes these queries would surface; if a rule here changes, that
// migration's WHEN clause needs the same edit.
export const WATCHED_AUDIT_PAIRS = [
  { entityType: 'invoice', eventType: 'voided' },
  { entityType: 'employee', eventType: 'role_changed' },
  { entityType: 'employee', eventType: 'mfa_disabled' },
  { entityType: 'bulk_op', eventType: 'bulk_delete_catalog_run' },
  { entityType: 'auth', eventType: 'login_failed' },
  { entityType: 'auth', eventType: 'mfa_disabled' },
  { entityType: 'employee', eventType: 'invited' },
  // KAN-661/662 — the 4 pairs the DB ping trigger gained; keep both in sync.
  { entityType: 'employee', eventType: 'ownership_transferred' },
  { entityType: 'employee', eventType: 'deactivated' },
  { entityType: 'employee', eventType: 'deleted' },
  { entityType: 'settings', eventType: 'mfa_policy_changed' },
  // RD-SEC-049 — single catalog deletes now count toward bulk_catalog_delete.
  { entityType: 'catalog', eventType: 'deleted' },
  // KAN-784 — a credit grant above the company's alert threshold
  // (migration 20261013130200 adds the same pair to the ping trigger).
  { entityType: 'invoice', eventType: 'customer_credit_large_grant' },
  // KAN-784 follow-up — an owner loosened a credit limit (20261013130400).
  { entityType: 'settings', eventType: 'credit_limits_raised' },
];

// In-memory cache keyed by tenantId — the dashboard row + modal both read
// this on every mount/tab-return; 60s TTL matches the retired tenantAlerts.js
// cache (loose enough to avoid hammering audit_events, tight enough that an
// admin watching an incident still sees a new burst near-real-time).
const SECURITY_EVENTS_CACHE_TTL_MS = 60 * 1000;
const cache = new Map();

// RD-PERF-003: last detection result per tenant, ANY age — the dashboard row
// seeds from it so a warm return paints the last known list at once (the 60s
// TTL above still decides whether a mount re-detects). Memory only; cleared at
// sign-out via clearSecurityEventsMemory(). Written under the same
// reviewGeneration guard as `cache` (a stale post-review result never lands)
// AND an `epoch` guard (bumped by clearSecurityEventsMemory, so a detection
// that started before a sign-out writes/publishes nothing after it).
const lastResult = new Map();
let epoch = 0;

/** Last known detection result for `tenantId` (any age) or null. */
export function peekSecurityEvents(tenantId) {
  return (tenantId && lastResult.get(tenantId)) || null;
}

/** Sign-out hygiene: drop every cached/in-flight/last result. */
export function clearSecurityEventsMemory() {
  epoch += 1;
  cache.clear();
  inflight.clear();
  lastResult.clear();
}

/** Force the next detectSecurityEvents call for this tenant to bypass the
 *  cache — used after markSecurityEventsReviewed succeeds. */
export function invalidateSecurityEventsCache(tenantId) {
  if (tenantId) cache.delete(tenantId);
}

// Two readers mount at once on the admin home — the dashboard row and the
// Panel nav badge — so a detection already in flight is SHARED, not repeated.
const inflight = new Map();

// Review bus: the row commits a review; every other reader for the same
// tenant (the nav badge) clears in the same tick, so the badge and the row
// can never disagree about whether anything is left to review.
const reviewListeners = new Set();

/** @returns {() => void} unsubscribe */
export function onSecurityReviewCommitted(listener) {
  reviewListeners.add(listener);
  return () => reviewListeners.delete(listener);
}

// Bumped on every committed review, so a detection that was already in flight
// when the review landed can't write its (now stale) events back into cache.
const reviewGeneration = new Map();

export function emitSecurityReviewCommitted(tenantId, watermark) {
  reviewGeneration.set(tenantId, (reviewGeneration.get(tenantId) || 0) + 1);
  inflight.delete(tenantId);
  invalidateSecurityEventsCache(tenantId);
  lastResult.delete(tenantId); // the reviewed list must not seed a later mount
  reviewListeners.forEach((listener) => {
    try { listener(tenantId, watermark); } catch (_e) { /* a listener never breaks the others */ }
  });
}

// Results bus: every `useSecurityEvents` reader (the dashboard row, the Panel
// nav badge) subscribes here — a DIFFERENT concern than `reviewListeners`
// above. The review bus says "a review just committed, clear your list"; this
// bus says "a fresh detection just ran, here is its result" and fires for
// EVERY detection (mount fetch, live ping, resume/focus, manual pull-to-
// refresh), so two readers can never independently settle on different
// results — the "row showed 2, badge showed nothing" bug (2026-09-26).
const resultListeners = new Map(); // tenantId -> Set<(result) => void>

/**
 * Subscribe to every fresh detection result for `tenantId`. Called by every
 * `useSecurityEvents` instance.
 * @returns {() => void} unsubscribe
 */
let receiptsUnsubscribe = null;

export function subscribeSecurityEvents(tenantId, listener) {
  if (!tenantId) return () => {};
  // One receipts-bus listener while ANY reader is mounted: a review made on
  // another device (NoticeReceiptsSync) re-derives every reader's list.
  if (!receiptsUnsubscribe) {
    receiptsUnsubscribe = subscribeNoticeReceipts(() => {
      Array.from(resultListeners.keys()).forEach(recomputeFromReceipts);
    });
  }
  let set = resultListeners.get(tenantId);
  if (!set) {
    set = new Set();
    resultListeners.set(tenantId, set);
  }
  set.add(listener);
  return () => {
    const current = resultListeners.get(tenantId);
    if (!current) return;
    current.delete(listener);
    if (current.size === 0) resultListeners.delete(tenantId);
    if (resultListeners.size === 0 && receiptsUnsubscribe) {
      receiptsUnsubscribe();
      receiptsUnsubscribe = null;
    }
  };
}

function publishSecurityEvents(tenantId, result) {
  const set = resultListeners.get(tenantId);
  if (!set || set.size === 0) return;
  set.forEach((listener) => {
    try { listener(result); } catch (_e) { /* one bad listener never breaks the others */ }
  });
}

/**
 * @param {string} since - ISO timestamp, the inclusive lower bound (30-day floor)
 * @returns {Promise<{ data: Array|null, error: any, truncated: boolean }>}
 */
async function fetchAuditEvents(tenantId, entityType, eventType, since) {
  const query = supabase
    .from('audit_events')
    .select('id, actor_id, actor_name, created_at, entity_id, payload')
    .eq('tenant_id', tenantId)
    .eq('entity_type', entityType)
    .eq('event_type', eventType)
    .gte('created_at', since);
  // Newest-first: if the PostgREST row cap truncates this rule's result, the
  // rows we KEEP must be the most recent ones — an ascending fetch capped at
  // 1000 would silently drop every event since, which is exactly backwards
  // for a security surface.
  const { data, error } = await query.order('created_at', { ascending: false });
  const truncated = Array.isArray(data) && data.length === POSTGREST_ROW_CAP;
  return { data, error, truncated };
}

/**
 * Run all 5 rules for `tenantId`. Each rule fails soft — if one query errors,
 * the others still produce results (mirrors the retired tenantAlerts.js
 * contract).
 *
 * @returns {Promise<{ events: Array, errors: Array, since: string,
 *   lastReview: string|null, truncated: boolean }>}
 */
export async function detectSecurityEvents(tenantId, { since: sinceOverride } = {}) {
  if (!tenantId) return { events: [], errors: [], since: null, lastReview: null, truncated: false };
  const cached = cache.get(tenantId);
  if (cached && Date.now() - cached.fetchedAt < SECURITY_EVENTS_CACHE_TTL_MS) {
    return cached.result;
  }
  if (!sinceOverride && inflight.has(tenantId)) return inflight.get(tenantId);
  if (sinceOverride) return runSecurityDetection(tenantId, sinceOverride);
  // Delete by IDENTITY: a review commit (or a later detection) may already
  // have replaced this tenant's entry — an older promise settling must not
  // evict the newer one, or the next reader starts a duplicate detection.
  const pending = runSecurityDetection(tenantId, null)
    .finally(() => { if (inflight.get(tenantId) === pending) inflight.delete(tenantId); });
  inflight.set(tenantId, pending);
  return pending;
}

/**
 * Bypasses the TTL cache and forces ONE fresh detection for `tenantId`,
 * publishing the result to every `subscribeSecurityEvents` listener (both the
 * dashboard row and the Panel badge, if mounted). Used by
 * `useSecurityEventsLive` (a ping on `security_event_pings`, a resumed tab, a
 * window focus) and by the admin dashboard's manual pull-to-refresh — a
 * manual refresh must never hand back stale, cached security events.
 *
 * Reuses the existing inflight dedupe: a ping arriving while a mount fetch
 * (or another ping) is already running joins that same in-flight promise
 * rather than firing a second set of queries.
 *
 * @param {string} tenantId
 * @returns {Promise<{ events: Array, errors: Array, since: string, lastReview: string|null, truncated: boolean }>}
 */
export function refreshSecurityEvents(tenantId) {
  // Nobody reading this tenant's security events (e.g. an `operations`
  // session sharing the admin dashboard's pull-to-refresh — useSecurityEvents
  // is admin-only) → nothing to update: skip the 8 queries instead of running
  // them RLS-denied and caching an empty result (security review P2).
  if (!tenantId || !(resultListeners.get(tenantId)?.size > 0)) {
    return Promise.resolve({
      events: [], errors: [], since: null, lastReview: null, truncated: false,
    });
  }
  invalidateSecurityEventsCache(tenantId);
  if (inflight.has(tenantId)) return inflight.get(tenantId);
  const pending = runSecurityDetection(tenantId, null)
    .finally(() => { if (inflight.get(tenantId) === pending) inflight.delete(tenantId); });
  inflight.set(tenantId, pending);
  return pending;
}

function sameIds(a, b) {
  return a.length === b.length && a.every((e, i) => e.id === b[i].id);
}

/** Candidates minus what THIS viewer already reviewed (one source for count, badge, row). */
function applyReceipts(candidates, viewerId) {
  const reviewed = viewerId ? readSecurityAlertReviewed(viewerId) : {};
  return {
    events: candidates.filter((e) => !reviewed[e.id]),
    lastReview: newestSecurityAlertReview(reviewed),
  };
}

/** A receipt arrived (another device, hydration): re-derive + republish. */
function recomputeFromReceipts(tenantId) {
  const prev = lastResult.get(tenantId);
  if (!prev?.viewerId) return;
  const { events, lastReview } = applyReceipts(prev.candidates, prev.viewerId);
  if (sameIds(events, prev.events) && lastReview === prev.lastReview) return;
  const result = { ...prev, events, lastReview };
  cache.set(tenantId, { result, fetchedAt: Date.now() });
  lastResult.set(tenantId, result);
  publishSecurityEvents(tenantId, result);
}

async function runSecurityDetection(tenantId, sinceOverride) {
  const generation = reviewGeneration.get(tenantId) || 0;
  const startEpoch = epoch;

  const now = Date.now();
  // Always the full 30-day window — never a review watermark floor.
  const since = sinceOverride || new Date(now - LOOKBACK_DAYS * DAY_MS).toISOString();

  // Order MUST match WATCHED_AUDIT_PAIRS — the destructure below relies on it.
  const [auditResults, viewer] = await Promise.all([
    Promise.all(WATCHED_AUDIT_PAIRS.map(({ entityType, eventType }) => (
      fetchAuditEvents(tenantId, entityType, eventType, since)
    ))),
    fetchViewerIdentity(),
  ]);
  const [
    voidRes, roleRes, mfaRes, bulkRes, loginRes, selfMfaRes, inviteRes,
    ownerRes, deactRes, removedRes, policyRes, catDelRes, largeCreditRes, limitsRes,
  ] = auditResults;
  const directory = await fetchMembershipDirectory(tenantId, viewer.userId);

  const errors = auditResults.filter((r) => r.error).map((r) => r.error);
  if (directory.error) errors.push(directory.error);
  // Never hide a truncated rule's events — newest-first keeps the recent ones;
  // the flag is for the caller to log/act on.
  const truncated = auditResults.some((r) => r.truncated);

  const detected = sortSecurityEvents([
    ...detectMassVoid(voidRes.data || []),
    ...detectRoleEscalation(roleRes.data || []),
    ...detectAdminInvites(inviteRes.data || []),
    // Tag each row with its source query BEFORE merging — admin reset vs
    // self-disable differ in entity_type (the Activity deep link needs it) and
    // the sweeper/employee pairing keys on it. Synthetic, never a column.
    ...detectMfaDisabled([
      ...(mfaRes.data || []).map((row) => ({ ...row, _sourceEntity: 'employee' })),
      ...(selfMfaRes.data || []).map((row) => ({ ...row, _sourceEntity: 'auth' })),
    ]),
    ...detectBulkCatalogDelete([
      ...(bulkRes.data || []).map((row) => ({ ...row, _sourceEntity: 'bulk_op' })),
      ...(catDelRes.data || []).map((row) => ({ ...row, _sourceEntity: 'catalog' })),
    ]),
    ...detectLoginFailedBurst(loginRes.data || []),
    ...detectOwnershipTransferred(ownerRes.data || []),
    ...detectAdminDeactivated(deactRes.data || [], directory.roleByUserId),
    ...detectAdminRemoved(removedRes.data || []),
    ...detectMfaPolicyChanged(policyRes.data || []),
    ...detectLargeCreditGrant(largeCreditRes.data || []),
    ...detectCreditLimitsRaised(limitsRes.data || []),
  ]);

  const relevant = filterEventsForViewer(detected, {
    userId: viewer.userId, email: viewer.email, joinedAt: directory.joinedAt,
  });
  const names = await fetchNamesByUserId(collectMissingNameIds(relevant));
  const candidates = fillEventNames(relevant, names);
  const { events, lastReview } = applyReceipts(candidates, viewer.userId);

  const result = {
    events, candidates, viewerId: viewer.userId, errors, since, lastReview, truncated,
  };
  if ((reviewGeneration.get(tenantId) || 0) === generation && startEpoch === epoch) {
    cache.set(tenantId, { result, fetchedAt: Date.now() });
    lastResult.set(tenantId, result);
    // Every detection broadcasts here so the row and the badge converge;
    // guarded like the cache write so a detection in flight when a review
    // committed never hands a stale result to readers that already cleared.
    publishSecurityEvents(tenantId, result);
  }
  return result;
}

/**
 * "Mark as reviewed": writes one per-user receipt for every alert in `events`
 * (local cache first, then server write-through). Never touches the list —
 * the caller commits that via `emitSecurityReviewCommitted`.
 *
 * @param {string} tenantId
 * @param {Array<{id: string}>} events - the alerts currently shown
 * @returns {Promise<{ data: string|null, error: any }>} data = review instant
 */
export async function markSecurityEventsReviewed(tenantId, events) {
  const viewerId = lastResult.get(tenantId)?.viewerId || (await fetchViewerIdentity()).userId;
  if (!viewerId) return { data: null, error: new Error('NO_VIEWER') };
  const nowMs = Date.now();
  markSecurityAlertsReviewed(viewerId, (events || []).map((e) => e.id), undefined, nowMs);
  return { data: new Date(nowMs).toISOString(), error: null };
}
